import React, { useCallback, useMemo, useRef } from "react";
import {
  ActionIcon,
  ActionIconsContainer,
  WheelCardContainer,
  CardImageContainer,
} from "./styles";
import TextView from "../../../TextView";
import { htmlToText } from "../../../../utils/helpers";
import WheelComponent from "../../../WheelComponent";
import { v4 as uuidv4 } from "uuid";
import { ASSETS } from "../../../../utils/config";
import IyugaTooltip from "../../../IyugaTooltip";
import CopiedFromCloneIcon from "../../../Icons/CopiedFromCloneIcon";
import { WheelCategories } from "../../../WheelGallery/WheelListing/TabComponents/WheelsTab";
import UserImage from "../../../UserImage";

export const WheelListingCard = ({
  wheel,
  openWheelDetails,
  selectedWheelCategory,
}) => {
  const wheelIdRef = useRef(uuidv4());

  // Visibility constants
  const PUBLIC_VISIBILITY = 1;
  const PRIVATE_VISIBILITY = 2;
  const HIDDEN_VISIBILITY = 3;

  const wheelUpdatedDataSorted = useMemo(() => {
    const segments = Array.isArray(wheel?.wheel_segments)
      ? wheel.wheel_segments
      : [];
    return [...segments].sort((a, b) => a.position - b.position);
  }, [wheel?.wheel_segments]);

  const getWheelBackground = useCallback((wallpaperSettings) => {
    const { bg_wallpaper, current_bg, segment_bg_color } =
      wallpaperSettings || {};
    return current_bg === 2 || current_bg === 3
      ? bg_wallpaper
      : segment_bg_color;
  }, []);

  const wheelProps = useMemo(
    () => ({
      userData: {
        id: wheelIdRef.current,
        url: wheel?.wheel_image || ASSETS.userPlaceholder,
      },
      maxSelectedArcs: 0,
      arcThickness: -10,
      chartData: wheelUpdatedDataSorted,
      margin: 25,
      setChartData: () => {},
      containerDivStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "90px",
        marginLeft: "5px",
      },
    }),
    [wheel?.wheel_image, wheelUpdatedDataSorted]
  );

  const visibility = wheel?.visibility || PRIVATE_VISIBILITY;

  return (
    <WheelCardContainer onClick={openWheelDetails}>
      <CardImageContainer
        bg={
          wheel?.version === 1
            ? wheel?.wallpaper
            : getWheelBackground(wheel?.wallpaper_settings)
        }
        style={{
          position: "relative",
        }}
      >
        <WheelComponent {...wheelProps} />
      </CardImageContainer>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: "8px",
          }}
        >
          <TextView
            variant="largeText"
            style={{
              fontWeight: 600,
              maxWidth: "60%",
              wordWrap: "break-word",
              lineHeight: "1.2",
              overflow: "hidden",
            }}
          >
            {htmlToText(wheel.wheel_name)}
          </TextView>

          <ActionIconsContainer
            style={{
              flex: 1,
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {/* Visibility Icons */}
            <IyugaTooltip
              tooltip={
                <TextView>
                  {visibility === HIDDEN_VISIBILITY
                    ? "Hidden"
                    : visibility === PUBLIC_VISIBILITY
                    ? "Public"
                    : "Private"}
                </TextView>
              }
            >
              <ActionIcon
                src={
                  visibility === HIDDEN_VISIBILITY
                    ? ASSETS.segmentEditor.Incognito
                    : visibility === PUBLIC_VISIBILITY
                    ? ASSETS.segmentEditor.PublicWheel
                    : ASSETS.segmentEditor.PrivateWheel
                }
                alt="Visibility"
                style={{ width: "18px", height: "18px" }}
              />
            </IyugaTooltip>

            {/* Legacy Wheel Icon */}
            {wheel?.version === 1 && (
              <IyugaTooltip
                tooltip={
                  <TextView>Legacy Wheel - No Longer Supported</TextView>
                }
              >
                <ActionIcon
                  src={ASSETS.segmentEditor.OldWheels}
                  alt="Legacy Wheel"
                  style={{ width: "18px", height: "18px" }}
                />
              </IyugaTooltip>
            )}

            {/* Locked Icon */}
            {wheel?.is_locked && (
              <IyugaTooltip tooltip={<TextView>Locked</TextView>}>
                <ActionIcon
                  src={ASSETS.segmentEditor.LockWheel}
                  alt="Locked"
                  style={{ width: "18px", height: "18px" }}
                />
              </IyugaTooltip>
            )}

            {/* Cloned Icon */}
            {wheel?.is_cloned && (
              <IyugaTooltip tooltip={<TextView>Cloned</TextView>}>
                <ActionIcon
                  src={ASSETS.segmentEditor.CloneWheel}
                  alt="Cloned"
                  style={{ width: "18px", height: "18px" }}
                />
              </IyugaTooltip>
            )}

            {/* Copied from Clone Icon */}
            {wheel?.is_copied_from_clone && (
              <IyugaTooltip tooltip={<TextView>Edited from Clone</TextView>}>
                <CopiedFromCloneIcon width={18} height={18} />
              </IyugaTooltip>
            )}

            {/* Live and not cloned Icon */}
            {wheel?.is_live && !wheel?.is_cloned && (
              <IyugaTooltip
                tooltip={
                  <TextView>
                    Generates Sync Clones - will update automatically when the
                    Original Author synchronize changes.
                  </TextView>
                }
              >
                <ActionIcon
                  src={ASSETS.Sync2}
                  alt="Live Share"
                  style={{ width: "18px", height: "18px" }}
                />
              </IyugaTooltip>
            )}
          </ActionIconsContainer>
        </div>

        {selectedWheelCategory === WheelCategories.Cloned && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "4px",
              width: "100%",
            }}
          >
            <UserImage image={wheel?.wheel_author?.profile_image} size={24} />
            <TextView variant="text" style={{ color: "#666" }}>
              {wheel?.wheel_author?.first_name} {wheel?.wheel_author?.last_name}
            </TextView>
          </div>
        )}
      </div>
    </WheelCardContainer>
  );
};
